import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, MessageCircle, ArrowRight, BadgeCheck } from "lucide-react";
import { Reveal, SectionHeading, PrimaryButton, Marquee, CountUp, MaskedLines } from "../components/primitives";
import { Icon, LevelCard, StoryCard, FAQSection, CTASection } from "../components/sections";
import { SkillsVsConfidence, LessonSystem, DnaJourneyStrip, DprSection, PersonalizedSection, TrainerIndependence, DifferentSection, RealDriving, RtoSupport, SocialProof, SevenFeatures, VideoStories } from "../components/HomeSections";
import { HomeStorePromo } from "../store/components";
import { OwnCarSection } from "../components/HomeSections";
import { useLeadModal } from "../components/LeadModalContext";
import { useLanguage , tr } from "../lib/language";
import { SITE, IMAGES, STATS, PROBLEMS, PILLARS, AUDIENCES, LEVELS, SPECIAL_PROGRAMS, FAQS, waLink } from "../data/site";
import { useSuccessStories } from "../lib/useSuccessStories";
import { track } from "../lib/track";

const Hero = () => {
  const { open } = useLeadModal();
  const { t } = useLanguage();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-36, 36]);

  return (
    <section className="relative overflow-hidden pb-20 pt-32 md:pb-28 md:pt-44" data-testid="hero-section">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -left-40 top-10 h-[34rem] w-[34rem] rounded-full bg-dwc-soft" />
        <div className="absolute -right-48 -top-24 h-[38rem] w-[38rem] rounded-full bg-dwc-green/[0.07]" />
      </div>
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <Reveal>
              <p className="inline-flex items-center gap-2.5 rounded-full border border-dwc-green/25 bg-white px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.25em] text-dwc-green shadow-sm" data-testid="hero-eyebrow">
                <span className="h-1.5 w-1.5 rounded-full bg-dwc-crimson" aria-hidden="true" />
                Where Psychology Meets The Road
              </p>
            </Reveal>
            <h1 className="mt-7 text-4xl font-extrabold leading-[1.04] tracking-tight text-dwc-charcoal sm:text-5xl lg:text-[4.2rem]" data-testid="hero-heading">
              <MaskedLines
                delay={0.15}
                lines={t.heroLines.slice(0, 2)}
              />
              <MaskedLines
                delay={0.45}
                lineClassName="text-dwc-green"
                lines={t.heroLines.slice(2)}
              />
            </h1>
            <Reveal delay={0.7}>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-dwc-charcoal/70 md:text-lg">
                {t.heroSub}
              </p>
            </Reveal>
            <Reveal delay={0.8}>
              <div className="mt-9 flex flex-col gap-4 sm:flex-row">
                <PrimaryButton onClick={() => open()} testId="hero-book-assessment-btn">{t.book}</PrimaryButton>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-whatsapp-btn"
                  onClick={() => track("whatsapp_click", { source: "hero" })}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-dwc-green bg-white px-7 py-4 text-sm font-bold uppercase tracking-wide text-dwc-green transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-dwc-soft"
                >
                  <MessageCircle className="h-4 w-4" aria-hidden="true" />
                  {t.whatsapp}
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.9}>
              <ul className="mt-10 flex flex-wrap gap-x-7 gap-y-3" data-testid="hero-trust-indicators">
                {["10,288+ Learners Trained", "14+ Years of Experience", "8+ Professional Instructors", "100% Money-Back Guarantee"].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-[13px] font-bold text-dwc-charcoal/80">
                    <BadgeCheck className="h-4 w-4 text-dwc-green" aria-hidden="true" />
                    {t}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div ref={imgRef} className="relative">
            <motion.div style={{ y }} className="relative">
              <Reveal delay={0.3} y={50}>
                <div className="relative">
                  <div className="absolute -inset-4 rounded-[2.5rem] bg-dwc-green/10" aria-hidden="true" />
                  <img
                    src={IMAGES.hero}
                    alt="A confident DWC student standing with the DWC training car in Mumbai, Surat & Ahmedabad"
                    className="relative aspect-[4/5] w-full rounded-[2.5rem] object-cover shadow-[0_40px_90px_rgba(23,107,58,0.25)]"
                    loading="eager"
                    data-testid="hero-image"
                  />
                  <div
                    className="absolute -bottom-6 -left-4 flex items-center gap-4 rounded-2xl bg-dwc-green p-5 text-white shadow-[0_20px_50px_rgba(23,107,58,0.4)] sm:-left-8"
                    data-testid="hero-guarantee-badge"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 ring-1 ring-dwc-crimson/70">
                      <ShieldCheck className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-200">DWC Promise</span>
                      <span className="block text-sm font-extrabold">100% Money-Back Guarantee</span>
                      <span className="mt-1 block h-0.5 w-10 rounded-full bg-dwc-crimson" aria-hidden="true" />
                    </span>
                  </div>
                </div>
              </Reveal>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Stats = () => (
  <section className="border-y border-gray-100 bg-white py-16 md:py-20" data-testid="stats-section">
    <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-12 px-5 lg:grid-cols-4 lg:px-8">
      {STATS.map((s, i) => (
        <Reveal key={tr(s.label)} delay={i * 0.1}>
          <div className="border-l-2 border-dwc-green/20 pl-5">
            <p className="text-4xl font-black tracking-tight text-dwc-green md:text-5xl">
              <CountUp value={s.value} suffix={s.suffix} testId={`stat-value-${i}`} />
            </p>
            <p className="mt-2 text-[13px] font-bold uppercase tracking-wider text-dwc-charcoal/60">{tr(s.label)}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Problems = () => (
  <section className="bg-white py-24 md:py-32" data-testid="problems-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        eyebrow="The Problem"
        number="01"
        title="You Can Learn To Drive. But That Doesn't Always Mean You Feel Confident."
        text="Sometimes the problem is not your driving. The problem is what happens in your mind when you face real traffic."
      />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {PROBLEMS.map((p, i) => (
          <Reveal key={tr(p.title)} delay={(i % 3) * 0.1} className="h-full">
            <article
              data-testid={`problem-card-${i}`}
              className={`group flex h-full flex-col rounded-2xl border p-8 transition-[box-shadow,transform,border-color] duration-300 hover:-translate-y-1 ${
                p.accent
                  ? "border-dwc-crimson/25 bg-white shadow-[0_8px_30px_rgba(181,18,43,0.06)] hover:shadow-[0_20px_50px_rgba(181,18,43,0.12)]"
                  : "border-gray-100 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-dwc-green/30 hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]"
              }`}
            >
              <span className={`inline-flex h-12 w-12 items-center justify-center rounded-xl transition-colors duration-300 ${p.accent ? "bg-dwc-crimson/10 text-dwc-crimson" : "bg-dwc-soft text-dwc-green group-hover:bg-dwc-green group-hover:text-white"}`}>
                <Icon name={p.icon} />
              </span>
              <h3 className="mt-5 text-lg font-extrabold tracking-tight text-dwc-charcoal">{tr(p.title)}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-dwc-charcoal/65">{tr(p.text)}</p>
              {p.guide && (
                <Link to={`/blog/${p.guide}`} data-testid={`problem-guide-${i}`} className="group/g mt-5 inline-flex items-center gap-1.5 text-[12px] font-extrabold uppercase tracking-wide text-dwc-crimson/80 transition-colors hover:text-dwc-green">
                  {tr("Read the Guide")}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/g:translate-x-1" aria-hidden="true" />
                </Link>
              )}
            </article>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-10 text-center">
          <Link to="/driving-problems" data-testid="explore-more-problems-btn" className="group inline-flex items-center gap-2 rounded-xl border-2 border-dwc-green px-7 py-4 text-sm font-bold uppercase tracking-wide text-dwc-green transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-dwc-soft">
            {tr("Explore More Driving Problems")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const Psychology = () => (
  <section className="bg-dwc-soft py-24 md:py-32" data-testid="psychology-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        eyebrow="The DWC Approach"
        number="03"
        title="Driving Skills + Structured Training + Driving Psychology."
        text="DWC combines practical driving training, personalized learning, reaction and awareness training, confidence building and real road practice — with continued support throughout your journey."
      />
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {PILLARS.map((p, i) => (
          <Reveal key={p.num} delay={(i % 3) * 0.1} className="h-full">
            <article data-testid={`pillar-card-${i}`} className="group h-full rounded-2xl border border-dwc-green/10 bg-white p-7 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]">
              <p className="text-sm font-black tracking-[0.2em] text-dwc-green/40 transition-colors group-hover:text-dwc-crimson">{p.num}</p>
              <h3 className="mt-3 text-lg font-extrabold tracking-tight text-dwc-charcoal">{tr(p.title)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-dwc-charcoal/65">{tr(p.text)}</p>
            </article>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <div className="relative mt-16 overflow-hidden rounded-3xl bg-dwc-green p-10 text-center md:p-16 grain">
          <p className="text-2xl font-extrabold uppercase leading-snug tracking-tight text-white md:text-4xl text-balance">
            We don't just train your hands and feet.
            <span className="mt-2 block text-emerald-200">We train your <span className="underline decoration-dwc-crimson decoration-4 underline-offset-8">mind</span> for the road.</span>
          </p>
          <Link
            to="/driving-psychology"
            data-testid="psychology-learn-more-btn"
            className="group mt-8 inline-flex items-center gap-2 rounded-xl bg-white px-7 py-4 text-sm font-bold uppercase tracking-wide text-dwc-green transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-lg"
          >
            Explore Driving Psychology
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const Levels = () => (
  <section className="bg-dwc-soft py-24 md:py-32" data-testid="levels-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        eyebrow="The Three DNA Framework"
        number="05"
        title="Learning DNA. Reaction DNA. Confidence DNA."
        text="One progressive journey — from strong foundations to calm reactions to fully independent driving."
      />
      <div className="mt-14 grid gap-8 lg:grid-cols-3 lg:gap-6">
        {LEVELS.map((level, i) => (
          <LevelCard key={level.slug} level={level} index={i} />
        ))}
      </div>
      <DnaJourneyStrip />
    </div>
  </section>
);

const WhoWeHelp = () => (
  <section className="bg-white py-24 md:py-32" data-testid="who-we-help-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        eyebrow="Confidence For Every Driver"
        number="11"
        title="Which Driver Are You?"
        text="Not a problem list — a mirror. Find the learner profile that sounds like you, and see the DWC pathway built for it."
      />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {AUDIENCES.map((a, i) => (
          <Reveal key={tr(a.title)} delay={(i % 3) * 0.1} className="h-full">
            <article data-testid={`audience-card-${i}`} className="group flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-dwc-soft text-dwc-green transition-colors duration-300 group-hover:bg-dwc-green group-hover:text-white">
                <Icon name={a.icon} />
              </span>
              <h3 className="mt-5 text-base font-extrabold uppercase tracking-wide text-dwc-charcoal">{tr(a.title)}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-dwc-charcoal/65">{tr(a.text)}</p>
              <Link to={a.to} data-testid={`audience-link-${i}`} className="group/l mt-5 inline-flex items-center gap-1.5 text-[13px] font-extrabold uppercase tracking-wide text-dwc-green transition-colors hover:text-dwc-green-dark">
                {tr("Your Pathway:")} {tr(a.pathway)}
                <ArrowRight className="h-3.5 w-3.5 shrink-0 transition-transform duration-300 group-hover/l:translate-x-1" aria-hidden="true" />
              </Link>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const SpecialPrograms = () => (
  <section className="bg-dwc-soft py-24 md:py-32" data-testid="special-programs-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        eyebrow="Special Programs"
        number="12"
        title="Training Designed Around You"
        text="Different drivers need different training. DWC builds the program around the person, not the other way around."
      />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {SPECIAL_PROGRAMS.map((p, i) => (
          <Reveal key={tr(p.title)} delay={(i % 3) * 0.1} className="h-full">
            <Link
              to={p.to}
              data-testid={`special-program-${i}`}
              className="group flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[box-shadow,transform,border-color] duration-300 hover:-translate-y-1 hover:border-dwc-green/30 hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-dwc-soft text-dwc-green transition-colors duration-300 group-hover:bg-dwc-green group-hover:text-white">
                <Icon name={p.icon} />
              </span>
              <h3 className="mt-5 text-lg font-extrabold tracking-tight text-dwc-charcoal">{tr(p.title)}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-dwc-charcoal/65">{tr(p.text)}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-extrabold uppercase tracking-wide text-dwc-green">
                Learn More
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
              </span>
            </Link>
          </Reveal>
        ))}
        <Reveal delay={0.2} className="h-full">
          <div className="flex h-full flex-col justify-between rounded-2xl bg-dwc-charcoal p-8 text-white">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-dwc-crimson">RTO Support</p>
              <h3 className="mt-4 text-lg font-extrabold tracking-tight">Licence & RTO Guidance</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/65">Learning licence, permanent licence, renewals, updates and lifetime free RTO consulting.</p>
            </div>
            <Link to="/rto-consulting" data-testid="special-program-rto" className="group mt-6 inline-flex items-center gap-1.5 text-[13px] font-extrabold uppercase tracking-wide text-emerald-300">
              Explore RTO Support
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Guarantee = () => (
  <section className="bg-white pb-24 md:pb-32" data-testid="guarantee-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2.5rem] bg-dwc-green px-8 py-16 md:px-16 md:py-20 grain">
          <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10" aria-hidden="true" />
          <div className="relative grid items-center gap-10 lg:grid-cols-[auto_1fr_auto]">
            <span className="flex h-20 w-20 items-center justify-center rounded-3xl bg-white/10 ring-2 ring-dwc-crimson/70">
              <ShieldCheck className="h-10 w-10 text-white" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-200">16 — The Guarantee</p>
              <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-white md:text-4xl">100% Money-Back Driving Guarantee</h2>
              <p className="mt-2 text-sm font-bold uppercase tracking-[0.2em] text-emerald-200/90">We believe in our training.</p>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/85">
                That's why eligible students who complete the qualifying guaranteed program may be covered by our money-back guarantee, subject to the program terms and conditions.
              </p>
            </div>
            <Link
              to="/guarantee"
              data-testid="guarantee-learn-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-xl bg-white px-7 py-4 text-sm font-bold uppercase tracking-wide text-dwc-green shadow-lg transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5"
            >
              Read Guarantee Terms
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Stories = () => {
  const stories = useSuccessStories();
  return (
  <section className="bg-dwc-soft py-24 md:py-32" data-testid="stories-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <SectionHeading
          eyebrow="Student Success"
          number="14"
          title="Real Students. Real Cars. Real Confidence."
          text="See how learners from different backgrounds built their confidence with Drive With Confidence. Every story below is a genuine DWC student."
        />
        <Reveal delay={0.2}>
          <Link to="/success-stories" data-testid="view-all-stories-btn" className="group inline-flex shrink-0 items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-dwc-green transition-colors hover:text-dwc-green-dark">
            View All Student Stories
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <StoryCard story={stories[0]} featured index={0} />
        {stories.slice(1, 4).map((s, i) => (
          <StoryCard key={s.name} story={s} index={i + 1} />
        ))}
      </div>
    </div>
  </section>
  );
};

const FaqJsonLd = () => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    }}
  />
);

const Home = () => (
  <>
    <Hero />
    <Marquee items={["Learn", "React", "Control", "Confidence", "Where Psychology Meets The Road", "10,288+ Confident Drivers"]} />
    <Stats />
    <Problems />
    <SkillsVsConfidence />
    <Psychology />
    <VideoStories />
    <LessonSystem />
    <Levels />
    <DprSection />
    <PersonalizedSection />
    <TrainerIndependence />
    <DifferentSection />
    <OwnCarSection />
    <SevenFeatures />
    <RealDriving />
    <WhoWeHelp />
    <SpecialPrograms />
    <RtoSupport />
    <Stories />
    <SocialProof />
    <Guarantee />
    <HomeStorePromo />
    <Marquee dark={false} items={["Learning DNA", "Reaction DNA", "Confidence DNA", "33-Lesson System", "Personalized Learning"]} />
    <FAQSection faqs={FAQS} chapter="17" />
    <CTASection />
    <FaqJsonLd />
  </>
);

export default Home;
